import { useEffect } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Paperclip } from "lucide-react";
import FileOrScanInput from "./FileOrScanInput";
import useUploadQueue from "@/hooks/useUploadQueue";

const BLUE = "#2B579A";

/**
 * FileOrScanDialog — modal wrapper around <FileOrScanInput /> that owns its
 * own upload queue.
 *
 * Two modes, mutually exclusive:
 *
 *   1) Immediate uploads (Chat, AttachmentsPanel) — pass `uploader`. Every
 *      file picked/scanned starts uploading right away with live progression.
 *      Once every entry is `done`, `onAllDone()` is fired (typically to
 *      refresh a list). "Fermer" simply closes the dialog.
 *
 *   2) Deferred / batch confirm — pass `onConfirm(files: File[])`. Files
 *      accumulate as `pending`; the user hits "Ajouter (N)" to emit the whole
 *      batch back to the parent (which handles uploads itself). Rarely used
 *      now that immediate mode covers all sites — kept for future needs.
 */
export default function FileOrScanDialog({
  open,
  onOpenChange,
  uploader,           // async (file, { onProgress, signal }) → immediate mode
  onAllDone,          // called once every entry is "done" (immediate mode)
  onConfirm,          // batch callback (deferred mode)
  title = "Ajouter des fichiers",
  submitLabel = "Fermer",
  accept = "*/*",
  multiple = true,
  testIdPrefix = "fos-dialog",
}) {
  const q = useUploadQueue({ autoStart: !!uploader, uploader });

  // Reset queue whenever the modal (re)opens.
  useEffect(() => {
    if (!open) q.reset();
  }, [open, q]);

  // Fire onAllDone as soon as every entry has finished (immediate mode).
  useEffect(() => {
    if (!uploader) return;
    if (q.entries.length === 0) return;
    const all = q.entries.every((e) => ["done", "error", "canceled"].includes(e.status));
    const anyDone = q.entries.some((e) => e.status === "done");
    if (all && anyDone) onAllDone?.();
  }, [q, uploader, onAllDone]);

  const handleOpenChange = (v) => {
    if (!v) q.reset();
    onOpenChange?.(v);
  };

  const isImmediate = !!uploader;

  const activeCount = q.entries.filter((e) => e.status === "uploading").length;
  const doneCount = q.entries.filter((e) => e.status === "done").length;
  const pendingCount = q.entries.filter((e) => e.status === "pending").length;

  const confirm = async () => {
    if (!isImmediate && onConfirm && pendingCount > 0) {
      const files = q.entries.filter((e) => e.status === "pending").map((e) => e.file);
      await onConfirm(files);
    }
    onOpenChange?.(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-lg p-0 overflow-hidden gap-0"
        style={{
          background: "linear-gradient(180deg, #232323, #1B1B1B)",
          border: "1px solid #2E2E2E",
          borderRadius: 16,
          boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
        }}
        data-testid={`${testIdPrefix}-root`}
      >
        <DialogHeader className="px-6 pt-5 pb-3">
          <div className="flex items-center gap-3">
            <div
              className="flex items-center justify-center shrink-0"
              style={{
                width: 40, height: 40,
                background: `linear-gradient(135deg, ${BLUE}, #1E3E70)`,
                borderRadius: 10,
                boxShadow: "0 4px 12px rgba(43,87,154,0.4)",
              }}
            >
              <Paperclip size={18} color="#FFFFFF" />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-[18px] font-bold leading-tight">
                {title}
              </DialogTitle>
              <div className="text-[12px] text-muted-foreground">
                Parcourir un dossier pour joindre des fichiers.
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="px-6 pb-4 max-h-[70vh] overflow-y-auto">
          <FileOrScanInput
            entries={q.entries}
            onFiles={q.enqueue}
            onCancel={q.cancel}
            onRemove={q.remove}
            accept={accept}
            multiple={multiple}
            testIdPrefix={`${testIdPrefix}-picker`}
          />
        </div>

        <DialogFooter
          className="px-6 py-4 gap-2"
          style={{ background: "rgba(0,0,0,0.15)", borderTop: "1px solid rgba(255,255,255,0.05)" }}
        >
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            className="h-10 border-[rgba(255,255,255,0.12)] hover:bg-white/5"
            data-testid={`${testIdPrefix}-cancel`}
            disabled={activeCount > 0}
          >
            {activeCount > 0 ? `Envoi… (${activeCount})` : "Fermer"}
          </Button>
          {!isImmediate && (
            <Button
              onClick={confirm}
              disabled={pendingCount === 0}
              className="h-10 text-white border-0 disabled:opacity-50 px-5"
              style={{
                background: `linear-gradient(135deg, ${BLUE}, #1E3E70)`,
                boxShadow: "0 4px 12px rgba(43,87,154,0.4)",
              }}
              data-testid={`${testIdPrefix}-confirm`}
            >
              {submitLabel} ({pendingCount})
            </Button>
          )}
          {isImmediate && doneCount > 0 && (
            <div className="text-[11px] text-muted-foreground self-center">
              {doneCount} fichier{doneCount > 1 ? "s" : ""} envoyé{doneCount > 1 ? "s" : ""}
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
